<template>
  <div class="alarm-page">
    <div class="page-header">
      <h1 class="page-title">在线闹钟</h1>
      <p class="page-desc">闹钟 · 计时器 · 秒表</p>
    </div>

    <!-- Tab -->
    <div class="tab-bar">
      <button :class="['tab-btn', { active: activeTab === 'alarm' }]" @click="activeTab = 'alarm'">⏰ 闹钟</button>
      <button :class="['tab-btn', { active: activeTab === 'timer' }]" @click="activeTab = 'timer'">⏱ 计时器</button>
      <button :class="['tab-btn', { active: activeTab === 'stopwatch' }]" @click="activeTab = 'stopwatch'">⏱ 秒表</button>
    </div>

    <!-- ========== 闹钟 ========== -->
    <div v-if="activeTab === 'alarm'" class="panel">
      <div class="current-time-display">{{ currentTime }}</div>
      <div class="alarm-set-row">
        <input type="time" v-model="alarmTime" step="1" class="time-input" />
        <button class="action-btn primary" @click="setAlarm">{{ alarmActive ? '取消闹钟' : '设置闹钟' }}</button>
      </div>

      <transition name="fade">
        <div v-if="alarmActive" class="alarm-status">
          <div class="alarm-icon">⏰</div>
          <div>闹钟已设置：<strong>{{ alarmTime }}</strong></div>
          <div class="countdown">还有 <strong>{{ alarmCountdown }}</strong></div>
        </div>
      </transition>

      <transition name="fade">
        <div v-if="alarmRinging" class="alarm-ringing" @click="stopAlarm">
          <div class="ringing-icon">🔔</div>
          <div class="ringing-text">时间到！</div>
          <div class="ringing-hint">点击关闭闹钟</div>
        </div>
      </transition>
    </div>

    <!-- ========== 计时器 ========== -->
    <div v-if="activeTab === 'timer'" class="panel">
      <div class="timer-display">{{ timerDisplay }}</div>
      <div class="timer-set-row">
        <div class="timer-field">
          <label>时</label>
          <input type="number" v-model.number="timerHours" min="0" max="99" class="field-input" />
        </div>
        <div class="timer-field">
          <label>分</label>
          <input type="number" v-model.number="timerMinutes" min="0" max="59" class="field-input" />
        </div>
        <div class="timer-field">
          <label>秒</label>
          <input type="number" v-model.number="timerSeconds" min="0" max="59" class="field-input" />
        </div>
      </div>
      <div class="timer-actions">
        <button v-if="!timerRunning" class="action-btn primary" @click="startTimer">开始</button>
        <button v-if="timerRunning" class="action-btn" @click="pauseTimer">暂停</button>
        <button v-if="timerPaused" class="action-btn primary" @click="resumeTimer">继续</button>
        <button v-if="timerRunning || timerPaused" class="action-btn" @click="resetTimer">重置</button>
      </div>

      <transition name="fade">
        <div v-if="timerDone" class="alarm-ringing" @click="timerDone = false">
          <div class="ringing-icon">🔔</div>
          <div class="ringing-text">计时结束！</div>
          <div class="ringing-hint">点击关闭</div>
        </div>
      </transition>
    </div>

    <!-- ========== 秒表 ========== -->
    <div v-if="activeTab === 'stopwatch'" class="panel">
      <div class="timer-display">{{ stopwatchDisplay }}</div>
      <div class="timer-actions">
        <button v-if="!swRunning" class="action-btn primary" @click="startStopwatch">开始</button>
        <button v-if="swRunning" class="action-btn" @click="pauseStopwatch">暂停</button>
        <button v-if="swPaused" class="action-btn primary" @click="resumeStopwatch">继续</button>
        <button v-if="swRunning || swPaused" class="action-btn" @click="lapStopwatch">计次</button>
        <button v-if="swRunning || swPaused || swLaps.length" class="action-btn" @click="resetStopwatch">重置</button>
      </div>

      <div v-if="swLaps.length" class="laps-list">
        <div class="laps-header">计次记录</div>
        <div v-for="(lap, i) in swLaps" :key="i" class="lap-item">
          <span>#{{ i + 1 }}</span>
          <span class="lap-time">{{ lap }}</span>
        </div>
      </div>
    </div>

    <!-- 隐藏的音频 -->
    <audio ref="alarmAudio" loop>
      <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACAf39/f4B/f3+Af4B/gH+Af4B/gH+Af39/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39/f39/f39/f3+Af4B/gH+Af4B/gH+Af4B/f39/f39/f39/gH+Af4B/gH+Af4B/gH+Af39" type="audio/wav" />
    </audio>
  </div>
</template>

<script>
export default {
  name: 'AlarmPage',
  data() {
    return {
      activeTab: 'alarm',
      currentTime: '',
      clockTimer: null,

      // 闹钟
      alarmTime: '08:00',
      alarmActive: false,
      alarmCountdown: '',
      alarmRinging: false,
      alarmInterval: null,

      // 计时器
      timerHours: 0,
      timerMinutes: 5,
      timerSeconds: 0,
      timerRemaining: 0,
      timerRunning: false,
      timerPaused: false,
      timerDone: false,
      timerInterval: null,

      // 秒表
      swRunning: false,
      swPaused: false,
      swElapsed: 0,
      swStartTime: 0,
      swInterval: null,
      swLaps: []
    }
  },
  computed: {
    timerDisplay() {
      const t = this.timerRemaining
      const h = Math.floor(t / 3600)
      const m = Math.floor((t % 3600) / 60)
      const s = t % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    stopwatchDisplay() {
      const t = Math.floor(this.swElapsed / 1000)
      const h = Math.floor(t / 3600)
      const m = Math.floor((t % 3600) / 60)
      const s = t % 60
      const ms = Math.floor((this.swElapsed % 1000) / 10)
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(ms).padStart(2, '0')}`
    }
  },
  mounted() {
    this.updateClock()
    this.clockTimer = setInterval(this.updateClock, 1000)
  },
  beforeUnmount() {
    if (this.clockTimer) clearInterval(this.clockTimer)
    if (this.alarmInterval) clearInterval(this.alarmInterval)
    if (this.timerInterval) clearInterval(this.timerInterval)
    if (this.swInterval) clearInterval(this.swInterval)
  },
  methods: {
    updateClock() {
      const pad = (n) => String(n).padStart(2, '0')
      const now = new Date()
      this.currentTime = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
      if (this.alarmActive) {
        this.updateCountdown(now)
      }
    },
    // ===== 闹钟 =====
    setAlarm() {
      if (this.alarmActive) {
        this.alarmActive = false
        this.alarmRinging = false
        if (this.alarmInterval) clearInterval(this.alarmInterval)
        this.stopAudio()
        return
      }
      this.alarmActive = true
      this.alarmRinging = false
      this.alarmInterval = setInterval(() => {
        const now = new Date()
        const pad = (n) => String(n).padStart(2, '0')
        const current = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
        if (current >= this.alarmTime + ':00') {
          this.triggerAlarm()
        }
        this.updateCountdown(now)
      }, 1000)
    },
    updateCountdown(now) {
      const target = new Date()
      const [h, m] = this.alarmTime.split(':')
      target.setHours(parseInt(h), parseInt(m), 0, 0)
      let diff = target - now
      if (diff < 0) diff += 86400000
      const dh = Math.floor(diff / 3600000)
      const dm = Math.floor((diff % 3600000) / 60000)
      const ds = Math.floor((diff % 60000) / 1000)
      this.alarmCountdown = `${dh}时${dm}分${ds}秒`
    },
    triggerAlarm() {
      this.alarmRinging = true
      this.playAudio()
      if (this.alarmInterval) clearInterval(this.alarmInterval)
    },
    stopAlarm() {
      this.alarmRinging = false
      this.alarmActive = false
      this.stopAudio()
    },
    playAudio() {
      try { this.$refs.alarmAudio.play() } catch (e) { /* ignore */ }
    },
    stopAudio() {
      try { this.$refs.alarmAudio.pause(); this.$refs.alarmAudio.currentTime = 0 } catch (e) { /* ignore */ }
    },

    // ===== 计时器 =====
    startTimer() {
      const total = this.timerHours * 3600 + this.timerMinutes * 60 + this.timerSeconds
      if (total <= 0) return
      this.timerRemaining = total
      this.timerRunning = true
      this.timerPaused = false
      this.timerDone = false
      this.timerInterval = setInterval(() => {
        this.timerRemaining--
        if (this.timerRemaining <= 0) {
          this.timerRemaining = 0
          this.timerRunning = false
          this.timerDone = true
          clearInterval(this.timerInterval)
          this.playAudio()
        }
      }, 1000)
    },
    pauseTimer() {
      this.timerRunning = false
      this.timerPaused = true
      clearInterval(this.timerInterval)
    },
    resumeTimer() {
      this.timerRunning = true
      this.timerPaused = false
      this.timerInterval = setInterval(() => {
        this.timerRemaining--
        if (this.timerRemaining <= 0) {
          this.timerRemaining = 0
          this.timerRunning = false
          this.timerDone = true
          clearInterval(this.timerInterval)
          this.playAudio()
        }
      }, 1000)
    },
    resetTimer() {
      this.timerRunning = false
      this.timerPaused = false
      this.timerDone = false
      this.timerRemaining = 0
      clearInterval(this.timerInterval)
    },

    // ===== 秒表 =====
    startStopwatch() {
      this.swRunning = true
      this.swPaused = false
      this.swStartTime = Date.now() - this.swElapsed
      this.swInterval = setInterval(() => {
        this.swElapsed = Date.now() - this.swStartTime
      }, 10)
    },
    pauseStopwatch() {
      this.swRunning = false
      this.swPaused = true
      clearInterval(this.swInterval)
    },
    resumeStopwatch() {
      this.swRunning = true
      this.swPaused = false
      this.swStartTime = Date.now() - this.swElapsed
      this.swInterval = setInterval(() => {
        this.swElapsed = Date.now() - this.swStartTime
      }, 10)
    },
    lapStopwatch() {
      this.swLaps.unshift(this.stopwatchDisplay)
    },
    resetStopwatch() {
      this.swRunning = false
      this.swPaused = false
      this.swElapsed = 0
      this.swLaps = []
      clearInterval(this.swInterval)
    }
  }
}
</script>

<style scoped>
.alarm-page { max-width: 600px; margin: 0 auto; padding: 20px 16px 30px; }

.page-header { text-align: center; padding: 24px 0 12px; }
.page-title { font-size: 26px; font-weight: 700; color: #1a1a2e; }
.page-desc { font-size: 14px; color: #888; margin-top: 6px; }

.tab-bar { display: flex; gap: 0; margin-bottom: 20px; background: #fff; border-radius: 10px; overflow: hidden; box-shadow: 0 1px 6px rgba(0,0,0,0.06); }
.tab-btn { flex: 1; padding: 12px 0; font-size: 14px; font-weight: 600; border: none; background: transparent; color: #888; cursor: pointer; transition: all 0.2s; }
.tab-btn.active { color: #fff; background: #1677ff; }
.tab-btn:hover:not(.active) { background: #f5f7fa; }

.panel { background: #fff; border-radius: 12px; padding: 30px 24px; text-align: center; box-shadow: 0 2px 12px rgba(0,0,0,0.06); }

.current-time-display {
  font-size: 48px; font-weight: 300;
  font-family: 'SF Mono', 'Consolas', 'Menlo', monospace;
  color: #1a1a2e; margin-bottom: 20px;
}

.alarm-set-row, .timer-set-row, .timer-actions {
  display: flex; justify-content: center; gap: 12px; align-items: center; flex-wrap: wrap;
}

.time-input { padding: 10px 16px; font-size: 20px; border: 2px solid #e0e0e0; border-radius: 8px; text-align: center; }

.timer-field { display: flex; flex-direction: column; gap: 4px; align-items: center; }
.timer-field label { font-size: 12px; color: #888; }
.field-input { width: 72px; padding: 10px 8px; font-size: 20px; border: 2px solid #e0e0e0; border-radius: 8px; text-align: center; }

.action-btn {
  padding: 10px 28px; font-size: 15px; font-weight: 600; border: 1px solid #e0e0e0;
  border-radius: 8px; background: #fff; color: #555; cursor: pointer; transition: all 0.2s;
}
.action-btn:hover { background: #f5f5f5; }
.action-btn.primary { background: #1677ff; color: #fff; border-color: #1677ff; }
.action-btn.primary:hover { background: #4096ff; }

.alarm-status { margin-top: 20px; color: #555; font-size: 15px; }
.alarm-icon { font-size: 40px; margin-bottom: 6px; }
.countdown { margin-top: 6px; font-size: 18px; color: #1677ff; }

.alarm-ringing {
  margin-top: 20px; padding: 30px 20px;
  background: linear-gradient(135deg, #fff2f0, #ffe6e6);
  border: 2px solid #ff4d4f; border-radius: 12px; cursor: pointer;
  animation: pulse 1s ease-in-out infinite;
}
.ringing-icon { font-size: 50px; }
.ringing-text { font-size: 28px; font-weight: 700; color: #ff4d4f; margin: 8px 0; }
.ringing-hint { font-size: 13px; color: #999; }

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.03); }
}

.timer-display {
  font-size: 64px; font-weight: 300;
  font-family: 'SF Mono', 'Consolas', 'Menlo', monospace;
  color: #1a1a2e; margin-bottom: 24px;
}
.timer-actions { margin-top: 16px; }

/* 计次 */
.laps-list { margin-top: 20px; text-align: left; }
.laps-header { font-size: 14px; font-weight: 600; color: #555; margin-bottom: 8px; text-align: center; }
.lap-item {
  display: flex; justify-content: space-between; padding: 8px 16px;
  font-size: 14px; font-family: 'SF Mono', 'Consolas', 'Menlo', monospace;
  color: #666; border-bottom: 1px solid #f0f0f0;
}
.lap-item:first-child { color: #1677ff; font-weight: 600; }
.lap-time { color: #333; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

@media (max-width: 480px) {
  .timer-display { font-size: 42px; }
  .current-time-display { font-size: 36px; }
  .time-input { font-size: 16px; }
  .field-input { width: 56px; font-size: 16px; }
}
</style>
